import { z } from 'zod';
import { isHungryrootAppUserAgent } from '@hungryroot/environment';
import { type Experiment, type ExperimentsResponse } from '@hungryroot/rest';
import { type EnrollmentMethod } from '@hungryroot/segment';
import { type ExperimentVariants, type Experiments } from './experiments';

/**
 * Shared enrollment primitives used by both the client-side enrollment flow
 * (`client-side-enrollment/enroll.ts`) and the Astro SSR enrollment flow
 * (`@hungryroot/springroll/.../enroll-server.ts`). Keep anything added here
 * transport-agnostic (no `js-cookie`, no Astro cookies, no React) so both
 * contexts can import it safely.
 */

export interface ExperimentEnrollment {
  experimentId: number;
  variant: number;
  /**
   * How the user landed on this variant. Persisted to the cookie alongside
   * `experimentId`/`variant` so the bulk anonymous `segment.identify` call
   * in `<Segment />` can re-emit the same `enrollment_method` tag that
   * `trackExperimentEnrollment` sends on the first (fresh) enrollment.
   *
   * Snake-cased to match the analytics team's convention for the
   * `enrollment_method` Segment trait. The cookie key is the same string,
   * so the value travels through to Segment without any property mapping.
   *
   * Optional because legacy cookies written before this field existed do
   * not carry it — those entries are forwarded to Segment without the tag
   * rather than backfilled with a fabricated value.
   */
  enrollment_method?: EnrollmentMethod;
}

/**
 * A pending tracking event emitted by SSR enrollment that is replayed by
 * the `DeferredExperimentTrackingIsland` once hydration completes. The
 * `enrollment_method` field is required here because every fresh enrollment
 * the SSR path queues for tracking has a known method — unlike the cookie
 * shape, where legacy entries may be missing it.
 */
export interface PendingTrackingEnrollment extends ExperimentEnrollment {
  enrollment_method: EnrollmentMethod;
}

export const cookieSchema = z
  .object({
    enrollments: z.array(
      z.object({
        experimentId: z.number(),
        variant: z.number(),
        enrollment_method: z
          .union([
            z.literal('random'),
            z.literal('forced'),
            z.literal('override'),
          ])
          .optional(),
      })
    ),
  })
  .catch({ enrollments: [] });

export type ExperimentEnrollments = z.infer<typeof cookieSchema>;

/**
 * Experiments are stored in cookies / the backend using a composite id where
 * the first digits are the canonical experiment id (e.g. 239) and the trailing
 * two digits identify the specific enrollment row (e.g. 23901, 23912). These
 * helpers centralize the arithmetic so the math is only defined in one place.
 */
export function toCanonicalId(experimentId: number): number {
  return Math.floor(experimentId / 100);
}

export function toStoredId(canonicalId: number): number {
  return canonicalId * 100;
}

export function toCanonicalVariant(variant: number): number {
  return variant % 10;
}

export function selectRandom<T extends number>(arr: readonly T[]): T {
  const { length } = arr;
  if (length === 1) {
    return arr[0];
  }
  const index = Math.floor(Math.random() * length);
  return arr[index];
}

export function findExperimentDefinition<T extends Experiments>(
  exp: T,
  resp: ExperimentsResponse
) {
  const { results } = resp;

  let match: Experiment | undefined;

  results.forEach((result) => {
    const expId = exp * 100;
    if (result.id >= expId && result.id <= expId + 99) {
      match = result;
    }
  });

  if (match === undefined) {
    return null;
  }

  const {
    variants,
    id: experimentId,
    control_weight: controlWeight,
    holdout_weight: holdoutWeight,
  } = match;
  if (variants === null) {
    return null;
  }

  return {
    serverDefinition: match,
    experimentId,
    controlWeight,
    // Coerce a missing field (older API payloads or test fixtures predating
    // `holdout_weight`) to `null` so callers can rely on the nullable type.
    holdoutWeight: holdoutWeight ?? null,
    variants: variants
      .split(',')
      .map((variantStr) => parseInt(variantStr, 10))
      .filter((value) => isNaN(value) === false) as ExperimentVariants<T>[],
  };
}

/**
 * Sentinel variant assigned to users bucketed into the holdout slice. Shares
 * the `-1` code with the pre-existing webview opt-out (see `ExcludeVariants`
 * in `experiments.ts`) and the post-signup backend holdout. Both meanings
 * resolve to "excluded from the variant under test" downstream, so the
 * overload is intentional — analytics treats `variant=-1` uniformly.
 */
export const HOLDOUT_VARIANT = -1;

/**
 * Rolls the holdout dice using the same `holdoutWeight%`-of-total semantics
 * as the post-signup backend (`app/models/customer.py`): the holdout check
 * runs against the full 100% before any control/treatment weighting is
 * applied. Returns the `HOLDOUT_VARIANT` sentinel when the user lands in the
 * holdout slice; `null` otherwise (caller proceeds to weighted enrollment).
 *
 * - `null` / `0` → never holds out
 * - `100` → always holds out
 * - Stickiness is the caller's responsibility — once a holdout assignment is
 *   persisted (cookie / backend row) it must not be re-rolled on weight
 *   changes; only an experiment rev releases existing holdouts.
 */
export function rollHoldout(
  holdoutWeight: Experiment['holdout_weight']
): typeof HOLDOUT_VARIANT | null {
  if (holdoutWeight === null || holdoutWeight <= 0) {
    return null;
  }
  if (Math.random() * 100 < holdoutWeight) {
    return HOLDOUT_VARIANT;
  }
  return null;
}

/**
 * Builds a weighted variants array suitable for uniform random selection
 * via `selectRandom`. The canonical control variant — the one for which
 * `toCanonicalVariant` returns `0` — is repeated `controlWeight` times
 * and the remaining variants split the residual `100 - controlWeight`
 * evenly.
 *
 * Identifying the control through `toCanonicalVariant` (rather than
 * checking `variant === 0` inline) keeps the iteration-encoding math in
 * one place per the helper's contract above, and ensures rev'd
 * experiments with variants like `[10, 11]` or `[20, 21, 22]` honor
 * `controlWeight` the same way iteration-0 variants do.
 *
 * Returns `variants` unchanged when:
 *   - `controlWeight` is `null` (no weighting requested)
 *   - the rev has no canonical control variant (e.g. `[11, 12]` —
 *     treatments-only)
 */
export function getWeightedVariants<T extends Experiments>(
  variants: ExperimentVariants<T>[],
  controlWeight: Experiment['control_weight']
) {
  if (controlWeight === null) {
    return variants;
  }

  const controlValue = variants.find(
    (variant) => toCanonicalVariant(variant) === 0
  );
  if (controlValue === undefined) {
    return variants;
  }

  if (controlWeight === 100) {
    return [controlValue];
  }

  const numVariants = variants.length - 1;
  const variantWeight = Math.round((100 - controlWeight) / numVariants);

  let weightedVariants: ExperimentVariants<T>[] = [];

  for (let i = 0, len = variants.length; i < len; i += 1) {
    const variant = variants[i];
    const arrayLen = variant === controlValue ? controlWeight : variantWeight;
    weightedVariants = weightedVariants.concat(
      Array.from({ length: arrayLen }, () => variant)
    );
  }

  return weightedVariants;
}

/**
 * Detects embedded webview contexts from a raw User-Agent header string. Used
 * by the SSR enrollment path where `window.navigator` is not available.
 *
 * Delegates to the shared `isHungryrootAppUserAgent` utility so this check
 * cannot drift from the client-side `useIsEmbeddedWebView` hook — they are
 * required to agree, otherwise the same user can receive different exclusion
 * verdicts depending on which page they entered through.
 */
export function isEmbeddedWebViewFromUserAgent(userAgent: string): boolean {
  return isHungryrootAppUserAgent(userAgent);
}

/**
 * UTM parameter names. Duplicated from `@hungryroot/segment`
 * (`packages/segment/src/funnelAnalytics/defs.ts`) because the segment
 * package's main entry eagerly initializes `AnalyticsBrowser`, which breaks
 * Node/SSR import. Keep these in sync; if more than this single duplication
 * appears, factor a `@hungryroot/segment/defs` subpath export instead.
 */
const UTM_PARAMS = {
  campaign: 'utm_campaign',
  medium: 'utm_medium',
  source: 'utm_source',
  term: 'utm_term',
  content: 'utm_content',
} as const;

interface UtmDataType {
  campaign: string | null;
  medium: string | null;
  source: string | null;
  term: string | null;
  content: string | null;
}

export function parseUtmFromUrl(url: URL): UtmDataType {
  return {
    campaign: url.searchParams.get(UTM_PARAMS.campaign),
    medium: url.searchParams.get(UTM_PARAMS.medium),
    source: url.searchParams.get(UTM_PARAMS.source),
    term: url.searchParams.get(UTM_PARAMS.term),
    content: url.searchParams.get(UTM_PARAMS.content),
  };
}

/**
 * A visitor's settled enrollment in a single experiment, in canonical form:
 * `experiment` and `variant` are decoded from the iteration encoding the
 * cookie stores. Read-only input to cross-experiment `forceVariant` rules.
 */
interface ExistingEnrollment {
  experiment: number;
  variant: number;
}

/**
 * The environment passed to `forceVariant` predicates on experiment
 * definitions. Identical shape on client and SSR enrollment paths so the
 * same predicate can run in both contexts without branching.
 */
export interface ForceVariantEnv {
  isEmbeddedWebView: boolean;
  url: URL;
  utm: UtmDataType;
  referer: string | null;
  /**
   * The visitor's already-settled enrollments in OTHER experiments, in
   * canonical form. Populated read-only from the same enrollment cookie both
   * the SSR and client paths already read before consulting `forceVariant`, so
   * it changes nothing about how enrollment itself works — it only lets a
   * predicate see cross-experiment state (e.g. mutual exclusion via
   * `forceExcludeIfEnrolledIn`).
   */
  existingEnrollments: readonly ExistingEnrollment[];
}

/**
 * Lowercased `utm_source` tokens that identify paid ad traffic. Matched
 * as substrings by `forceFromUtmSource` so suffixed variants like
 * `facebook-sub_q4-2026` are caught by the bare `facebook-sub` token.
 * Used to keep paid ad clicks out of experiment variants on pre-signup
 * pages so analytics aren't muddied by traffic with different conversion
 * characteristics.
 *
 * Curated with marketing — keep this list in sync with the `utm_source`
 * values used by active paid campaigns. Tokens should be the most
 * specific stable prefix shared across that platform's campaigns.
 */
export const PAID_AD_SOURCES: readonly string[] = [
  'applovin',
  'facebook-sub',
  'facebook-dir',
  'google',
  'google-other',
  'tiktok',
  'snap-ads',
  'nextdoor-sub',
];

/**
 * Returns `true` if `url.pathname` matches any of the supplied path
 * prefixes. A prefix matches if the pathname equals the prefix exactly,
 * equals the prefix followed by a trailing slash, or starts with the
 * prefix followed by a `/` segment boundary. The segment boundary check
 * avoids false positives such as `/quizzical` matching the `/quiz` prefix.
 *
 * Trailing slashes on the prefix are normalized away (`/quiz` and `/quiz/`
 * behave identically).
 */
export function matchesAnyPathPrefix(
  url: URL,
  pathPrefixes: readonly string[]
): boolean {
  const { pathname } = url;
  return pathPrefixes.some((prefix) => {
    const normalized = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
    return (
      pathname === normalized ||
      pathname === `${normalized}/` ||
      pathname.startsWith(`${normalized}/`)
    );
  });
}

/**
 * Higher-order helper that gates a `forceVariant` predicate on the URL's
 * pathname. The wrapped predicate is only invoked when the user is on one
 * of `pathPrefixes`. On any other path this returns `undefined` so the
 * standard random enrollment runs.
 *
 * Use this for experiments whose override policy only applies to specific
 * pre-signup pages — e.g. force paid traffic into control on `/quiz`, but
 * use random enrollment everywhere else the experiment is exposed.
 *
 * @example
 *   forceVariant: isRouteMatch(['/quiz'], forcePaidSourcesToControl()),
 */
export function isRouteMatch<R>(
  pathPrefixes: readonly string[],
  forceVariantFn: (env: ForceVariantEnv) => R
): (env: ForceVariantEnv) => R | undefined {
  return (env) => {
    if (!matchesAnyPathPrefix(env.url, pathPrefixes)) {
      return undefined;
    }
    return forceVariantFn(env);
  };
}

/**
 * Returns a `forceVariant` predicate that forces the user into `variant`
 * when the landing URL's `utm_source` *contains* any of the supplied
 * `sources` tokens as a substring. Comparison is case-insensitive.
 *
 * Substring matching exists because marketing routinely appends a
 * campaign / creative / influencer suffix to the platform-level source
 * (e.g. `utm_source=facebook-cookbook_plant-based-juniors` for an
 * influencer-driven cookbook ad). Listing only the stable platform
 * prefix here keeps experiment definitions terse and resilient to new
 * suffix variants without code changes.
 *
 * Returns `undefined` when `utm_source` is absent or doesn't contain
 * any token, letting downstream rules — or the standard random
 * enrollment — run.
 *
 * Caller responsibility: pass tokens in lowercase (the URL value is
 * lowercased before comparison; the tokens are not).
 *
 * Compose with `isRouteMatch` to scope to specific routes, and with
 * `firstMatch` to chain multiple source-based overrides.
 *
 * @example
 *   // Matches `applovin`, `applovin-ad`, `tiktok-q4-2026`, etc.
 *   forceFromUtmSource({ sources: PAID_AD_SOURCES, variant: 0 })
 *
 *   // Matches `facebook-cookbook`, `facebook-cookbook_<influencer>`, etc.
 *   forceFromUtmSource({ sources: ['facebook-cookbook'], variant: 1 })
 */
export function forceFromUtmSource<const V extends number>({
  sources,
  variant,
}: {
  sources: readonly string[];
  variant: V;
}): (env: ForceVariantEnv) => V | undefined {
  return ({ utm }) => {
    const source = utm.source?.toLowerCase();
    if (source === undefined) {
      return undefined;
    }
    if (sources.some((token) => source.includes(token))) {
      return variant;
    }
    return undefined;
  };
}

/**
 * Returns a `forceVariant` predicate that forces the user into `variant`
 * when the visit looks like direct-organic traffic — i.e. no UTM params
 * of any kind on the URL AND no referer. An empty-string referer is
 * treated the same as `null` since `document.referrer` returns `''` for
 * truly direct visits while the SSR `Referer` header is absent (`null`).
 *
 * Note: this does NOT detect paid traffic that arrives via auto-tagged
 * click IDs (`gclid`, `fbclid`, etc.) without UTMs. If you need to keep
 * such traffic out of the override, list those sources in a preceding
 * `forceFromUtmSource` rule via `firstMatch`, or extend this helper.
 */
export function forceWhenDirectOrganic<const V extends number>({
  variant,
}: {
  variant: V;
}): (env: ForceVariantEnv) => V | undefined {
  return ({ utm, referer }) => {
    const hasAnyUtm =
      utm.source !== null ||
      utm.medium !== null ||
      utm.campaign !== null ||
      utm.term !== null ||
      utm.content !== null;
    if (hasAnyUtm) {
      return undefined;
    }
    if (referer !== null && referer !== '') {
      return undefined;
    }
    return variant;
  };
}

/**
 * Composes multiple `forceVariant` predicates with first-match-wins
 * semantics. Each predicate is invoked in order; the first one to return
 * a non-`undefined` value short-circuits and that value is returned. If
 * every predicate returns `undefined`, the composed predicate returns
 * `undefined` so the standard random enrollment runs.
 *
 * Predicate order is significant. Put the most specific or highest-
 * priority rules first.
 *
 * @example
 *   forceVariant: firstMatch([
 *     forceFromUtmSource({ sources: PAID_AD_SOURCES, variant: 0 }),
 *     isRouteMatch(['/quiz'], forceWhenDirectOrganic({ variant: 0 })),
 *     isRouteMatch(
 *       ['/cookbook'],
 *       forceFromUtmSource({ sources: ['facebook-cookbook'], variant: 1 })
 *     ),
 *   ]),
 */
export function firstMatch<R>(
  predicates: readonly ((env: ForceVariantEnv) => R | undefined)[]
): (env: ForceVariantEnv) => R | undefined {
  return (env) => {
    for (const predicate of predicates) {
      const result = predicate(env);
      if (result !== undefined) {
        return result;
      }
    }
    return undefined;
  };
}

/**
 * Returns a `forceVariant` predicate that excludes the visitor from THIS
 * experiment — forcing `excludeVariant`, a negative `ExcludeVariants` code —
 * when they already hold a settled enrollment in `otherExperimentId`. Use it
 * to make two experiments mutually exclusive: whichever one the visitor
 * enrolls in first wins, and the second read forces them out so they see
 * control and drop out of that experiment's analytics.
 *
 * "Settled enrollment" means any real variant (`variant >= 0`, control
 * included). A prior *exclusion* in the other experiment (a negative code)
 * deliberately does NOT trigger exclusion here — otherwise the two would
 * exclude each other into an empty intersection.
 *
 * Reads only `existingEnrollments` (the visitor's already-settled enrollments
 * from the cookie), so it is identical on the client and SSR paths. It relies
 * on the two experiments enrolling on different surfaces, never in the same
 * request — so the other experiment's enrollment is always already written by
 * the time this runs, and there is no same-request race.
 *
 * Returns `undefined` when the visitor is not enrolled in `otherExperimentId`,
 * letting the standard random/holdout enrollment run.
 *
 * @example
 *   // On experiment 255: exclude anyone already enrolled in 220.
 *   forceVariant: forceExcludeIfEnrolledIn(220, -2)
 */
export function forceExcludeIfEnrolledIn(
  otherExperimentId: number,
  excludeVariant: -1 | -2 | -3
): (env: ForceVariantEnv) => -1 | -2 | -3 | undefined {
  return ({ existingEnrollments }) =>
    existingEnrollments.some(
      (enrollment) =>
        enrollment.experiment === otherExperimentId && enrollment.variant >= 0
    )
      ? excludeVariant
      : undefined;
}
